import { AzureOpenAI } from 'openai';

// Azure OpenAI configuration
const endpoint = process.env.REACT_APP_AZURE_OPENAI_ENDPOINT;
const apiKey = process.env.REACT_APP_AZURE_OPENAI_API_KEY;
const deploymentName = process.env.REACT_APP_AZURE_OPENAI_DEPLOYMENT;

export interface MarketingSummary {
  headline: string;
  summary: string;
  keyFeatures: string[];
  socialMediaPost: string;
  emailSubject: string;
  emailBody: string;
}

/**
 * Summarizes a release note for marketing purposes using Azure OpenAI
 * @param releaseVersion - The version number of the release
 * @param releaseDescription - The technical release notes/description
 * @param applicationName - The name of the application
 * @returns A marketing-friendly summary of the release
 */
export async function summarizeReleaseForMarketing(
  releaseVersion: string,
  releaseDescription: string,
  applicationName: string
): Promise<MarketingSummary> {
  // Check if Azure OpenAI is configured
  if (!endpoint || !apiKey || !deploymentName) {
    throw new Error('Azure OpenAI is not configured. Please set REACT_APP_AZURE_OPENAI_ENDPOINT, REACT_APP_AZURE_OPENAI_API_KEY, and REACT_APP_AZURE_OPENAI_DEPLOYMENT in your .env file.');
  }

  const client = new AzureOpenAI({
    endpoint,
    apiKey,
    deployment: deploymentName,
    apiVersion: '2024-08-01-preview',
    dangerouslyAllowBrowser: true
  });

  const prompt = `You are a marketing content specialist. Create marketing materials for the following software release:

Application: ${applicationName}
Version: ${releaseVersion}
Release Notes: ${releaseDescription}

Please provide the following marketing content in JSON format:
{
  "headline": "A catchy, attention-grabbing headline (max 100 characters)",
  "summary": "A compelling 2-3 sentence summary highlighting the value and benefits to users",
  "keyFeatures": ["3-5 key features or improvements in bullet-point format, focusing on benefits"],
  "socialMediaPost": "A engaging social media post (max 280 characters) with emojis",
  "emailSubject": "A compelling email subject line (max 60 characters)",
  "emailBody": "A professional email announcement (3-4 paragraphs) with clear value propositions"
}

Make the content engaging, benefit-focused, and suitable for non-technical audiences. Focus on how users will benefit from this release.`;

  try {
    const response = await client.chat.completions.create({
      model: deploymentName,
      messages: [
        {
          role: 'system',
          content: 'You are a professional marketing content writer specializing in software product releases. You excel at translating technical features into customer benefits.'
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      max_tokens: 1500,
      temperature: 0.7,
      top_p: 0.95,
      response_format: { type: 'json_object' }
    });

    const content = response.choices[0]?.message?.content;
    if (!content) {
      throw new Error('No response from Azure OpenAI');
    }

    const marketingSummary: MarketingSummary = JSON.parse(content);
    return marketingSummary;
  } catch (error: any) {
    console.error('Error calling Azure OpenAI:', error);
    throw new Error(`Failed to generate marketing summary: ${error.message}`);
  }
}

/**
 * Gets the latest release from an array of releases
 */
export function getLatestRelease(releases: any[]): any | null {
  if (!releases || releases.length === 0) {
    return null;
  }

  // Sort by release date (most recent first)
  const sorted = [...releases].sort((a, b) => {
    return new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime();
  });

  return sorted[0];
}
